import React, { useCallback, useEffect, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { CommanServices } from '@/services/commanServices';
import socketService from '@/services/socketService';
import ScenarioCandidateSourceChips from './ScenarioCandidateSourceChips';

const CHECK_TONES = {
  active: 'border-cyan-200 bg-cyan-50 text-cyan-700',
  expired: 'border-slate-200 bg-slate-100 text-slate-600',
  replaced: 'border-amber-200 bg-amber-50 text-amber-700',
};

const RESULT_TONES = {
  in_duty: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  not_in_duty: 'border-rose-200 bg-rose-50 text-rose-700',
  pending: 'border-amber-200 bg-amber-50 text-amber-700',
  no_response: 'border-slate-200 bg-slate-100 text-slate-600',
  cancelled: 'border-slate-200 bg-slate-100 text-slate-600',
};

const resultLabel = (status) => ({
  in_duty: 'Ready',
  not_in_duty: 'Unavailable',
  pending: 'Pending',
  no_response: 'No response',
  cancelled: 'Not answered · check replaced',
}[String(status || '').toLowerCase()] || 'Not checked');

const checkLabel = (status) => ({
  active: 'Live',
  expired: 'Expired',
  replaced: 'Replaced',
}[String(status || '').toLowerCase()] || 'Completed');

const formatDateTime = (value) => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Time unavailable';
  return parsed.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const readinessPercentageForCheck = (check = {}) => {
  const stats = check?.stats || {};
  const total = Math.max(0, Number(stats.total ?? check.target_count) || 0);
  const ready = Math.max(0, Number(stats.in_duty) || 0);
  return total ? Math.round((ready / total) * 100) : 0;
};

export const readinessResultCounts = (check = {}) => {
  const stats = check?.stats || {};
  return {
    ready: Number(stats.in_duty) || 0,
    pending: Number(stats.pending) || 0,
    unavailable: Number(stats.not_in_duty) || 0,
    noResponse: (Number(stats.no_response) || 0) + (Number(stats.cancelled) || 0),
  };
};

export default function IncidentReadinessLog({ incident }) {
  const [sectionOpen, setSectionOpen] = useState(true);
  const [checks, setChecks] = useState([]);
  const [expandedCheck, setExpandedCheck] = useState('');
  const [details, setDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadHistory = useCallback(async ({ silent = false } = {}) => {
    if (!incident?.id) return;
    if (!silent) setLoading(true);
    try {
      const response = await CommanServices.getIncidentReadinessHistory(incident.id);
      if (response?.status === false) throw new Error(response?.message || 'Unable to load readiness history');
      setChecks(Array.isArray(response?.data?.items) ? response.data.items : []);
      setError('');
    } catch (loadError) {
      setError(loadError?.message || 'Unable to load readiness history');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [incident?.id]);

  const loadDetails = useCallback(async (checkId) => {
    if (!checkId) return;
    setDetails((current) => ({ ...current, [checkId]: { ...(current[checkId] || {}), loading: true, error: '' } }));
    try {
      const response = await CommanServices.getDutyCheckResponses(checkId);
      if (response?.status === false) throw new Error(response?.message || 'Unable to load readiness results');
      setDetails((current) => ({
        ...current,
        [checkId]: { loading: false, error: '', rows: response?.data?.rows || [] },
      }));
    } catch (loadError) {
      setDetails((current) => ({
        ...current,
        [checkId]: { loading: false, error: loadError?.message || 'Unable to load readiness results', rows: [] },
      }));
    }
  }, []);

  useEffect(() => {
    setChecks([]);
    setDetails({});
    setExpandedCheck('');
    loadHistory();
  }, [incident?.id, loadHistory]);

  useEffect(() => {
    if (!incident?.id) return undefined;
    return socketService.on('staffing_readiness_updated', (event) => {
      if (String(event?.incident_id || '') !== String(incident.id)) return;
      loadHistory({ silent: true });
      if (event?.check_id && String(event.check_id) === String(expandedCheck)) loadDetails(event.check_id);
    });
  }, [expandedCheck, incident?.id, loadDetails, loadHistory]);

  const totalChecks = checks.length;
  const toggleDetails = (checkId) => {
    if (expandedCheck === checkId) {
      setExpandedCheck('');
      return;
    }
    setExpandedCheck(checkId);
    if (!details[checkId]?.rows) loadDetails(checkId);
  };

  return (
    <section className="mb-6" data-testid="incident-readiness-log">
      <button
        type="button"
        onClick={() => setSectionOpen((current) => !current)}
        className="flex w-full items-center gap-2 rounded-lg p-2 text-left transition hover:bg-cyan-50"
        aria-expanded={sectionOpen}
        data-testid="incident-readiness-log-toggle"
      >
        <span className="text-2xl" aria-hidden="true">📡</span>
        <span className="text-sm font-semibold text-gray-800">Incident Readiness Log</span>
        <span className="text-xs text-gray-500">({totalChecks})</span>
        <ChevronDown className={`ml-auto h-4 w-4 text-gray-500 transition-transform ${sectionOpen ? 'rotate-180' : ''}`} />
      </button>

      {sectionOpen && (
        <div className="ml-2 mt-2 space-y-2">
          {loading && <p className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-4 text-xs text-gray-500">Loading readiness checks…</p>}
          {error && <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-3 text-xs text-rose-700">{error}</p>}
          {!loading && !error && checks.length === 0 && (
            <p className="rounded-lg border border-dashed border-gray-200 px-3 py-4 text-xs italic text-gray-500">No incident readiness checks sent yet.</p>
          )}

          {checks.map((check, index) => {
            const counts = readinessResultCounts(check);
            const percentage = Number.isFinite(Number(check.readiness_percentage))
              ? Number(check.readiness_percentage)
              : readinessPercentageForCheck(check);
            const isExpanded = expandedCheck === check.id;
            const detail = details[check.id] || {};
            const responseWindow = check.response_window_minutes !== null
              && check.response_window_minutes !== undefined
              && check.response_window_minutes !== ''
              && Number.isFinite(Number(check.response_window_minutes))
              ? `${Number(check.response_window_minutes)}m window`
              : 'Window unavailable';
            return (
              <article key={check.id} className="overflow-hidden rounded-lg border border-gray-200 bg-white" data-testid={`readiness-history-card-${check.id}`}>
                <div className="px-3 py-2.5" data-testid={`readiness-history-summary-${check.id}`}>
                  <div
                    className="flex flex-wrap items-center gap-x-2 gap-y-1 lg:flex-nowrap"
                    data-testid={`readiness-history-header-${check.id}`}
                  >
                    <p className="whitespace-nowrap text-xs font-bold text-gray-900">Check {totalChecks - index}</p>
                    <span className={`whitespace-nowrap rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase ${CHECK_TONES[String(check.status || '').toLowerCase()] || CHECK_TONES.expired}`}>{checkLabel(check.status)}</span>
                    {check.is_automatic && (
                      <span className="whitespace-nowrap rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 text-[9px] font-bold uppercase text-violet-700">
                        Automatic
                      </span>
                    )}
                    <span className="whitespace-nowrap text-[11px] font-black text-emerald-700">{percentage}% ready</span>
                    <button
                      type="button"
                      onClick={() => toggleDetails(check.id)}
                      className="ml-auto inline-flex h-7 shrink-0 items-center gap-1 whitespace-nowrap rounded-md px-2 text-[10px] font-bold text-cyan-700 transition hover:bg-cyan-50"
                      aria-expanded={isExpanded}
                      data-testid={`readiness-history-details-${check.id}`}
                    >
                      {isExpanded ? 'Hide people' : 'View people'}
                      <ChevronDown className={`h-3 w-3 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                    </button>
                  </div>

                  <div
                    className="mt-1.5 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[10px] text-gray-400 lg:flex-nowrap"
                    data-testid={`readiness-history-metadata-${check.id}`}
                  >
                    <span className="whitespace-nowrap">{formatDateTime(check.created_at)}</span>
                    <span aria-hidden="true">·</span>
                    <span className="whitespace-nowrap">{check.sent_by_name || 'Administrator'}</span>
                    <span aria-hidden="true">·</span>
                    <span className="whitespace-nowrap">{responseWindow}</span>
                    <span aria-hidden="true">·</span>
                    <span
                      className="flex flex-wrap items-center gap-x-1.5 gap-y-1 lg:flex-nowrap"
                      data-testid={`readiness-history-counts-${check.id}`}
                    >
                      {[
                        ['Ready', counts.ready, 'text-emerald-700'],
                        ['Pending', counts.pending, 'text-amber-700'],
                        ['Unavailable', counts.unavailable, 'text-rose-700'],
                        ['No response', counts.noResponse, 'text-slate-600'],
                      ].map(([label, value, textTone], outcomeIndex) => (
                        <React.Fragment key={label}>
                          {outcomeIndex > 0 && <span aria-hidden="true">·</span>}
                          <span className="inline-flex items-center gap-1 whitespace-nowrap" data-readiness-outcome={label.toLowerCase().replace(' ', '-')}>
                            <strong className={textTone}>{value}</strong>
                            <span> {label}</span>
                          </span>
                        </React.Fragment>
                      ))}
                    </span>
                  </div>
                </div>

                {isExpanded && (
                  <div className="divide-y divide-gray-100 border-t border-gray-100 bg-gray-50/40" data-testid={`readiness-history-people-${check.id}`}>
                    {detail.loading && <p className="px-2 py-3 text-center text-xs text-gray-400">Loading results…</p>}
                    {detail.error && <p className="rounded-md bg-rose-50 px-2 py-2 text-xs text-rose-700">{detail.error}</p>}
                    {!detail.loading && !detail.error && (detail.rows || []).map((row) => {
                      const status = String(row.status || '').toLowerCase();
                      return (
                        <div key={row.user_id} className="flex flex-col gap-1.5 px-3 py-2 sm:flex-row sm:items-start sm:justify-between" data-testid={`readiness-history-person-${row.user_id}`}>
                          <div className="min-w-0">
                            <div className="flex min-w-0 flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
                              <span className="text-xs font-semibold text-gray-900">{row.user_name}</span>
                              {(row.proposed_roles || []).length > 0 && <span className="text-[10px] text-gray-500">· {row.proposed_roles.join(', ')}</span>}
                            </div>
                            <ScenarioCandidateSourceChips value={row} dark={false} className="mt-1" />
                          </div>
                          <div className="flex shrink-0 items-center gap-2 sm:justify-end">
                            <span className={`rounded-full border px-1.5 py-0.5 text-[9px] font-bold ${RESULT_TONES[status] || RESULT_TONES.no_response}`}>{resultLabel(status)}</span>
                            {row.responded_at && <span className="text-[9px] text-gray-400">Responded {formatDateTime(row.responded_at)}</span>}
                          </div>
                        </div>
                      );
                    })}
                    {!detail.loading && !detail.error && detail.rows && detail.rows.length === 0 && <p className="px-2 py-3 text-center text-xs text-gray-400">No recipients recorded.</p>}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}
